import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppPricingService, FieldMask } from '@galaxy/marketplace-apps';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AppPrices } from '@vendasta/marketplace-apps';
import { PackageService, ShoppingCartItemType, StoreService } from '@vendasta/marketplace-packages';
import {
  CategoryToItemListMap,
  StoreItem,
  StorefrontCategory,
  convertStoreProductToStoreItemWithAppPrice,
} from '@vendasta/store';
import {
  Observable,
  catchError,
  combineLatest,
  first,
  map,
  of,
  shareReplay,
  switchMap,
  BehaviorSubject,
  tap,
} from 'rxjs';
import { take } from 'rxjs/operators';
import { Package } from '../services/package/package';

const ALL_CATEGORY_ID = '__ALL__';

@Component({
  selector: 'strfrnt-tile-view',
  templateUrl: './tile-view.component.html',
  styleUrls: ['./tile-view.component.scss'],
  standalone: false,
})
export class TileViewComponent implements OnInit {
  marketId$: Observable<string>;
  partnerId$: Observable<string>;
  storeItemsMap$: Observable<CategoryToItemListMap>;
  categories$: Observable<StorefrontCategory[]>;

  private currency$$ = new BehaviorSubject<string>(null);
  currency$ = this.currency$$.asObservable();

  get baseUrl(): Observable<string> {
    return this.route.params.pipe(
      map((params) =>
        params['marketId']
          ? `/public/store/${params['partnerId']}/${params['marketId']}`
          : `/public/store/${params['partnerId']}`,
      ),
    );
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private packageService: PackageService,
    private alertService: SnackbarService,
    private storeService: StoreService,
    private appPricingService: AppPricingService,
  ) {}

  private initializeObservables(): void {
    this.partnerId$ = this.route.paramMap.pipe(map((paramMap) => paramMap.get('partnerId')));
    this.marketId$ = this.route.paramMap.pipe(map((paramMap) => paramMap.get('marketId')));
  }

  ngOnInit(): void {
    this.initializeObservables();
    this.storeItemsMap$ = of(new Map<string, StoreItem[]>());

    this.categories$ = combineLatest([this.partnerId$, this.marketId$, this.route.queryParamMap]).pipe(
      switchMap(([partnerId, marketId, queryParamMap]) => {
        marketId = marketId ? marketId : 'default'; //set default.
        const breakCache = !!queryParamMap.get('breakCache') || false;
        return this.storeService.getStoreWithCategories(partnerId, marketId, breakCache, false);
      }),
      map((resp) => resp.categories || []),
      catchError((err) => {
        if (err?.status !== 404) {
          this.alertService.openErrorSnack('Oops! Something went wrong showing this store!');
        }
        return of([]);
      }),
      map((categories) => {
        const cats = categories.map((cat) => {
          return {
            id: cat.categoryId,
            name: cat.categoryName,
            packageIds: cat.packageIds || [],
          };
        });
        this.onNeedDataForCategory(cats.length > 0 ? cats[0] : null);
        return cats;
      }),
      catchError(() => {
        this.alertService.openErrorSnack('Oops! Something went wrong showing this store!');
        return of([]);
      }),
      shareReplay(1),
    );
  }

  // Extract and update the currency from store items
  private updateCurrency(storeItemsMap: CategoryToItemListMap): void {
    for (const storeItems of storeItemsMap.values()) {
      for (const storeItem of storeItems) {
        if (storeItem.appPrice) {
          const firstEdition = Object.keys(storeItem.appPrice.pricesForEditions)[0];
          if (storeItem.appPrice.pricesForEditions?.[firstEdition]?.pricesForCurrencies) {
            const currency = Object.keys(storeItem.appPrice.pricesForEditions[firstEdition].pricesForCurrencies)[0];
            this.currency$$.next(currency);
            return;
          }
        }
      }
    }
    // No currency found (null will default to USD)
    this.currency$$.next(null);
  }

  private getPackages(packageIds: string[]): Observable<Package[]> {
    if (packageIds.length < 1) {
      return of([]);
    }
    return this.packageService.getMulti(packageIds).pipe(
      catchError((err) => {
        if (err.status === 404) {
          this.alertService.openErrorSnack('This store is currently empty!');
          return of([]);
        }
        console.error(err);
        this.alertService.openErrorSnack('Oops! Something went wrong showing this store!');
        return of([]);
      }),
      map((pkgs) => (pkgs || []).map((pkg) => Package.fromApi(pkg)).filter((pkg) => pkg && pkg.status === 'PUBLISHED')),
      shareReplay(1),
    );
  }

  onProductSelected(values: { id: string; type: ShoppingCartItemType }): void {
    combineLatest([this.baseUrl, this.marketId$])
      .pipe(first())
      .subscribe(([baseUrl]) => {
        if (values.type === ShoppingCartItemType.SHOPPING_CART_ITEM_TYPE_PACKAGE) {
          this.router.navigateByUrl(`${baseUrl}/package/${values.id}`);
        } else if (values.type === ShoppingCartItemType.SHOPPING_CART_ITEM_TYPE_PRODUCT) {
          this.router.navigateByUrl(`${baseUrl}/app/${values.id}`);
        }
      });
  }

  onNeedDataForCategory(category: StorefrontCategory): void {
    this.storeItemsMap$ = this.storeItemsMap$.pipe(
      first(),
      switchMap((catMap) => {
        const categoryId = category?.id || ALL_CATEGORY_ID;
        if (categoryId === ALL_CATEGORY_ID) {
          return this.getProductsInAllCategory(catMap);
        } else {
          return this.getPackagesInCategory(categoryId, category.packageIds, catMap);
        }
      }),
      tap((storeItemsMap) => {
        this.updateCurrency(storeItemsMap);
      }),
    );
  }

  getProductsInAllCategory(catMap: CategoryToItemListMap): Observable<Map<string, StoreItem[]>> {
    return combineLatest([this.partnerId$, this.marketId$]).pipe(
      switchMap(([partnerId, marketId]) => {
        return this.storeService.listProductsInStore(partnerId, marketId, null, 1000, null).pipe(
          switchMap((resp) => {
            if ((resp.storeProducts ?? []).length === 0) {
              catMap.set(ALL_CATEGORY_ID, []);
              return of(catMap);
            }
            const productIds = resp.storeProducts.map((p) => p.appId || p.packageId);
            return this.appPricingService
              .getMultiPricing(productIds, new FieldMask({ paths: ['retail'] }), partnerId, marketId)
              .pipe(
                catchError((error) => {
                  console.error('Error fetching pricing data for ALL category:', error);
                  const storeItems = resp.storeProducts.map((sp) =>
                    convertStoreProductToStoreItemWithAppPrice(sp, null),
                  );
                  catMap.set(ALL_CATEGORY_ID, storeItems);
                  return of(catMap);
                }),
                map((appPricing) => {
                  const storeItems = resp.storeProducts.map((sp, index) => {
                    const appPrices = appPricing[index];
                    return convertStoreProductToStoreItemWithAppPrice(sp, appPrices);
                  });
                  catMap.set(ALL_CATEGORY_ID, storeItems);
                  return catMap;
                }),
                shareReplay({ refCount: true, bufferSize: 1 }),
              );
          }),
        );
      }),
    );
  }

  getPackagesInCategory(
    categoryId: string,
    packageIds: string[],
    catMap: CategoryToItemListMap,
  ): Observable<Map<string, StoreItem[]>> {
    const publishedPackages$ = this.getPackages(packageIds).pipe(take(1));

    return combineLatest([this.partnerId$, this.marketId$, publishedPackages$]).pipe(
      switchMap(([partnerId, marketId, pkgs]) => {
        const publishedPackageIds = pkgs.map((p) => p.solution_id);
        return this.appPricingService
          .getMultiPricing(publishedPackageIds, new FieldMask({ paths: ['retail'] }), partnerId, marketId)
          .pipe(
            catchError((error) => {
              console.error('Error fetching pricing data for category:', error);
              const storeItems = pkgs.map((p) => {
                return this.packageToStoreItem(p, null);
              });
              catMap.set(categoryId, storeItems);
              return of(catMap);
            }),
            map((appPricing) => {
              const storeItems = pkgs.map((p, index) => {
                const appPrices = appPricing[index];
                return this.packageToStoreItem(p, appPrices);
              });
              catMap.set(categoryId, storeItems);
              return catMap;
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
          );
      }),
    );
  }

  packageToStoreItem(pkg: Package, appPrices: AppPrices): StoreItem {
    return {
      name: pkg.name,
      tagline: pkg.tagline,
      headerImageUrl: pkg.header_image_url,
      iconUrl: pkg.icon,
      appPrice: appPrices?.pricesForContexts['retail'],
      id: pkg.solution_id,
      type: ShoppingCartItemType.SHOPPING_CART_ITEM_TYPE_PACKAGE,
    } as StoreItem;
  }
}
